import {
  Blockquote,
  Button,
  ButtonGroup,
  Checkbox,
  Container,
  Grid,
  NumberInput,
  Space,
  Stack,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconArrowLeft } from "@tabler/icons-react";
import { useListEvents } from "../hooks/useListEvents";
import { useNavigate, useParams } from "react-router";
import { electronicFormatIBAN, isValidIBAN } from "ibantools";
import { ibanBelongsToBanksGermany } from "../ibanBelongsToBank";
import { z } from "zod";
import { useRegister } from "../hooks/useRegister";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { trackEvent } from "../analytics.ts";

type FormInputs = {
  iban: string;
  firstname: string;
  lastname: string;
  email: string;
  gdpr: boolean;
  agb: boolean;
  member: boolean;
  pvAge?: number | string;
};

const schema = z.object({
  firstname: z
    .string()
    .min(1, { message: "Bitte geben Sie einen Vornamen ein" })
    .trim(),
  lastname: z
    .string()
    .min(1, { message: "Bitte geben Sie einen Nachnamen ein" })
    .trim(),
  iban: z
    .string()
    .min(1, "Bitte geben Sie Ihre IBAN ein")
    .refine((value) => {
      const formattedIban = electronicFormatIBAN(value);
      if (!formattedIban) {
        return false;
      }

      if (!isValidIBAN(formattedIban)) {
        return false;
      }

      const bankleitzahlen: string[] = ["49092650", "49262364"];
      return ibanBelongsToBanksGermany(formattedIban, bankleitzahlen);
    }, "Bitte geben Sie Ihre IBAN bei der Volksbank PLUS eG an"),
  email: z.email("Bitte geben Sie eine gültige E-Mail-Adresse ein"),
  gdpr: z.literal<boolean>(true, {
    error: () => ({
      message: "Bitte akzeptieren Sie die Datenschutzerklärung",
    }),
  }),
  member: z.literal<boolean>(true, {
    error: () => ({
      message:
        "Bitte bestätigen Sie, dass die angemeldete Person Volksbank PLUS Mitglied ist",
    }),
  }),
  agb: z.literal<boolean>(true, {
    error: () => ({
      message: "Bitte akzeptieren Sie die AGB",
    }),
  }),
  pvAge: z
    .union([z.number().min(1990).max(2025), z.string().length(0)])
    .optional(),
});

export const Formular = () => {
  const navigate = useNavigate();

  const params = useParams<"vortragId">();
  if (!params.vortragId) {
    throw new Error("eventId not given");
  }

  const events = useListEvents();
  const event = events.data.events.find((e) => e.id == params.vortragId);
  if (!event) {
    throw new Error("eventId not found");
  }

  const mutation = useRegister();

  const form = useForm<FormInputs>({
    initialValues: {
      iban: "",
      lastname: "",
      firstname: "",
      email: "",
      gdpr: false,
      agb: false,
      member: false,
    },
    validate: zod4Resolver(schema),
    validateInputOnBlur: true,
    transformValues: (values) => {
      const formattedIBAN = electronicFormatIBAN(values.iban);
      if (!formattedIBAN) {
        throw new Error("could not format iban");
      }

      return {
        ...values,
        iban: formattedIBAN,
      };
    },
  });

  const onSubmit = form.onSubmit(
    async (values) => {
      const additionalInfo: Record<string, string> = {};
      if (values.pvAge) {
        additionalInfo["pvAge"] = values.pvAge.toString();
      }

      trackEvent("anmeldung-abgeschickt", {
        vortragId: event.id,
        titel: event.title,
      });

      let response;
      try {
        response = await mutation.mutateAsync({
          firstName: values.firstname.trim(),
          lastName: values.lastname.trim(),
          iban: values.iban,
          eventId: params.vortragId ?? "",
          email: values.email.trim(),
          additionalInfo,
        });
      } catch (error) {
        trackEvent("anmeldung-fehlgeschlagen", {
          vortragId: event.id,
          grund: "netzwerkfehler",
        });
        throw error;
      }

      if (response.response.status == 409) {
        trackEvent("anmeldung-fehlgeschlagen", {
          vortragId: event.id,
          grund: "bereits-angemeldet",
        });
        form.setFieldError(
          "iban",
          "Sie sind bereits für einen Vortrag angemeldet. Pro Person können Sie nur an einem Vortrag teilnehmen.",
        );
        return;
      }

      if (!response.response.ok) {
        trackEvent("anmeldung-fehlgeschlagen", {
          vortragId: event.id,
          grund: `status-${response.response.status}`,
        });
      } else {
        trackEvent("anmeldung-erfolgreich", {
          vortragId: event.id,
          titel: event.title,
        });
      }

      navigate(`/anmelden/${params.vortragId}/danke`);
    },
    (errors) => {
      trackEvent("anmeldung-validierung-fehler", {
        vortragId: event.id,
        felder: Object.keys(errors).sort().join(","),
      });
    },
  );

  return (
    <>
      <Container size="md">
        <Button
          radius="xl"
          color="#303030"
          leftSection={<IconArrowLeft />}
          onClick={() => {
            trackEvent("vortrag-gewechselt", { vortragId: event.id });
            navigate("/");
          }}
        >
          Anderen Vortrag wählen
        </Button>
      </Container>

      <Space h="xl" />

      <Container size="md">
        <Title order={2} mb="lg">
          Anmeldung zum Vortrag „{event?.title}“
        </Title>
        <form onSubmit={onSubmit}>
          <Stack justify="flex-start" gap="lg">
            <Grid>
              <Grid.Col span={{ sm: 6, base: 12 }}>
                <TextInput
                  withAsterisk
                  label="Vorname"
                  placeholder="Toni"
                  {...form.getInputProps("firstname")}
                />
              </Grid.Col>
              <Grid.Col span={{ sm: 6, base: 12 }}>
                <TextInput
                  withAsterisk
                  label="Nachname"
                  placeholder="Raiffeisen"
                  {...form.getInputProps("lastname")}
                  width={400}
                />
              </Grid.Col>
            </Grid>

            <Blockquote>
              Bitte füllen Sie pro Person eine Anmeldung aus. Weitere Personen
              können Sie nach Abschluss dieser Anmeldung anmelden.
            </Blockquote>

            <TextInput
              label="E-Mail-Adresse"
              description="Wir senden eine Bestätigung an diese Adresse."
              placeholder="toni.raiffeisen@volksbank.de"
              withAsterisk
              type="email"
              {...form.getInputProps("email")}
            />
            <TextInput
              label="IBAN bei der Volksbank PLUS"
              description="Wir benötigen die IBAN zur Überprüfung der Mitgliedschaft bei der Volksbank PLUS."
              placeholder="DE12345678901234567890"
              withAsterisk
              {...form.getInputProps("iban")}
            />

            {event.id == "eda06ff8-7739-4ff0-ba32-71e9fdd9c08d" && (
              <NumberInput
                label="Baujahr Ihrer Photovoltaikanlage"
                description="Diese Angabe hilft, den Vortrag besser auf die Teilnehmenden zuzuschneiden."
                min={1990}
                max={2025}
                step={1}
                placeholder="2005"
                {...form.getInputProps("pvAge")}
              />
            )}

            <Checkbox
              mt="xl"
              name="member"
              label={
                <>
                  Ich bestätige, dass die angemeldete Person Mitglied der
                  Volksbank PLUS ist.
                </>
              }
              {...form.getInputProps("member", { type: "checkbox" })}
            />

            <Checkbox
              name="gdpr"
              label={
                <>
                  <a
                    target="_blank"
                    href="/VBP_2026_Datenschutzhinweise_Mitgliederwoche.pdf"
                    onClick={() =>
                      trackEvent("dokument-geoeffnet", {
                        dokument: "datenschutzerklaerung",
                      })
                    }
                  >
                    Datenschutzerklärung
                  </a>{" "}
                  gelesen und akzeptiert
                </>
              }
              {...form.getInputProps("gdpr", { type: "checkbox" })}
            />

            <Checkbox
              name="agb"
              label={
                <>
                  <a
                    target="_blank"
                    href="/VBP_2026_AGB_Mitgliederwoche.pdf"
                    onClick={() =>
                      trackEvent("dokument-geoeffnet", { dokument: "agb" })
                    }
                  >
                    AGB
                  </a>{" "}
                  gelesen und akzeptiert
                </>
              }
              {...form.getInputProps("agb", { type: "checkbox" })}
            />

            <ButtonGroup mt="xl">
              <Button
                type="submit"
                size="md"
                radius="xl"
                disabled={!event.freeSeats}
              >
                {event.freeSeats ? (
                  <>Anmeldung abschicken</>
                ) : (
                  <>Keine freien Plätze</>
                )}
              </Button>
            </ButtonGroup>
          </Stack>
        </form>
      </Container>
    </>
  );
};
